import * as React from "react";
import {
  Grid,
  Link,
  Modal,
  InputLabel,
  TextField,
  Snackbar,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

// helpers imports
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import { useState } from "react";
import { checkPrivileges } from "../utils/PrivilegesHelper";
import { PrivilegesContext } from "../PrivilegesContext";
import { Alert } from "@mui/material";
import GeneralModal from "./GeneralModal";
import { height } from "@mui/system";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import { normalizeWhitespaceForSave } from "../utils/WhitespaceHelper";
import { useFlash } from "../FlashContext";

interface Props {
  patient_id: string;
  modalOpen: boolean;
  setModalOpen: any;
  setRenderingKey?: any;
  showToast: (args: {
    type: "error" | "success" | "info" | "warning";
    title: string;
    text?: string;
    autoHideDuration?: number;
    actionLink?: {
      label: string;
      onClick: () => void;
    };
  }) => void;
  onSuccess?: React.Dispatch<React.SetStateAction<string>>;
}

// --- Constants ---
const TITLE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;



const useStyles = makeStyles(() => ({
  container: {
    left: 0,
    right: 0,
    marginLeft: "auto",
    marginRight: "auto",
    width: 500,
    marginTop: 80,
    "@media (max-width: 600px)": {
      width: "100%",
      marginTop: 70,
    },
  },
  pageTitle: {
    textAlign: "center",
    "& span": {
      font: "26px QuicksandMedium",
    },
  },
  backButton: {
    font: "30px QuicksandMedium",
    textDecoration: "none",
    display: "inline-block",
    marginTop: -6,
  },
  pageHeading: {
    borderBottom: "1px solid #948b87",
    marginBottom: 20,
    paddingBottom: 10,
  },
  centerText: {
    textAlign: "center",
  },
  infoSection: {
    width: "85%",
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 15,
    marginBottom: 15,
    boxShadow: "1px 1px 1px 1px #efefef",
    padding: 10,
  },
  nextButton: {
    background: "none",
    border: "none",
    padding: 10,
    paddingLeft: "30%",
    paddingRight: "30%",
    font: "inherit",
    cursor: "pointer",
    outline: "inherit",
    textAlign: "center",
    color: "#ffffff",
    backgroundColor: "#ff8906",
    borderRadius: 4,
    fontFamily: "QuicksandMedium",
    marginTop: 20,
    display: "inline-block",
    "&:hover": {
      textDecoration: "none",
    },
  },
  textInputLabel: {
    font: "12px QuicksandMedium",
    display: "inline-block",
    marginTop: 10,
  },
  textInput: {
    width: "100%",
    marginTop: 10,
    marginBottom: 10,
    borderRadius: 4,
    backgroundColor: "#fcf6f4",
  },
  centerButton: {
    textAlign: "center",
    marginTop: 10,
  },
  clearButtonStyling: {
    background: "none",
    color: "inherit",
    border: "none",
    padding: 0,
    font: "inherit",
    cursor: "pointer",
    outline: "inherit",
    textAlign: "center",
  },
  alert: {
    border: "1px solid #dbe3e6",
  },
}));

const FollowUpForm: React.FC<Props> = (props: Props) => {
  // authenticationContext and chat context and other contexts
  const authenticationSetting = React.useContext(AuthenticationContext);
  const [nextDate, setNextDate] = React.useState<any>(new Date());
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPatientRosterReminderAction, setIsPatientRosterReminderAction] = useState(false);
  const userPrivileges = React.useContext(PrivilegesContext);
  const [actionResourceItems, setActionResourceItems] = React.useState([]);
  const [error, setError] = React.useState("");
  const classes = useStyles();
  const { showToast } = useFlash();

  const handleDateChange = (date) => {
    setNextDate(date);
  };

  React.useEffect(() => {
    if (props.modalOpen) {
      getTempActionResources();
    }
  }, [props.modalOpen]);

  const getTempActionResources = () => {
    let tempActionResources = JSON.parse(
      sessionStorage.getItem("saved_action_resources_temp")
    );
    let tempIds = [];
    if (tempActionResources) {
      tempIds = tempActionResources.map((tempActionResource) => {
        return tempActionResource.resource_item_id;
      });
    }
    setError("");
    fetch(`/resource_items`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          showToast({
            title: "Failed to Fetch Resources",
            text: result.message || "Something went wrong while fetching resources",
            type: "error",
          });
        } else {
          let allResourceItems = result.resource;
          let selectedActionResources = allResourceItems.filter(
            (resourceItem) => tempIds.includes(resourceItem.id)
          );
          setActionResourceItems(selectedActionResources);
        }
      })
      .catch((error) => {
        showToast({
          title: "Failed to Fetch Resources",
          text: "Something went wrong while fetching resources",
          type: "error",
        });
      });
  };

  const validForm = () => {
    return !!nextDate && !!title?.trim();
  };

  const saveFollowUp = () => {
    if (!validForm()) {
      const missingFields = [];

      if (!title?.trim()) missingFields.push("title");
      if (!nextDate) missingFields.push("due date");

      setError("Invalid entries, please check your entries and try again.");

      const fieldLabel =
        missingFields.length === 1
          ? missingFields[0].charAt(0).toUpperCase() + missingFields[0].slice(1)
          : "Required fields";

      const message =
        missingFields.length === 1
          ? `Please provide a ${missingFields[0]} to create the quick action.`
          : "The title and due date need to be populated in order to create the quick action.";

      showToast({
        title: `${fieldLabel} ${missingFields.length === 1 ? "required" : "missing"}`,
        text: message,
        type: "error",
      });

      return;
    }

    const normalizedTitle = normalizeWhitespaceForSave(title);
    const normalizedDescription = normalizeWhitespaceForSave(description);

    fetch("/follow_up_dates", {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        follow_up_date: {
          patient_id: props.patient_id,
          next_date: nextDate,
          title: normalizedTitle,
          description: normalizedDescription,
        },
        quick_action: {
          patient_id: props.patient_id,
          next_date: nextDate,
          title: normalizedTitle,
          description: normalizedDescription,
          is_patient_roster_reminder_action: isPatientRosterReminderAction,
        },
        resource_items: actionResourceItems,
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          alert(result.error);
          return;
        }

        if (!result?.resource) {
          alert("Something is wrong and template cannot be saved");
          return;
        }

        props.onSuccess?.(result.resource.id);
        props.showToast({
          title: "Quick Action Created",
          text: "Quick Action created successfully",
          type: "success",
        });
        setIsPatientRosterReminderAction(false);
        props.setModalOpen(false);
        props.setRenderingKey?.(Math.random());
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const addResource = () => {
    sessionStorage.setItem(
      "patient_action_temp",
      JSON.stringify({
        text: title,
        subtext: description,
      })
    );
    window.location.href = `/resource-catalog?patient=${props.patient_id}&patient_action=new&redirect_url=/patient_reports/${props.patient_id}/new_action`;
  };

  const removeActionResourceItem = (resource) => {
    if (
      confirm("Are you sure you want to remove the resource from the action?")
    ) {
      let actionResourceItemsTemp = actionResourceItems;
      actionResourceItemsTemp = actionResourceItemsTemp.filter(
        (item) => item.id !== resource.id
      );
      setActionResourceItems(actionResourceItemsTemp);
    }
  };

  const closeModal = () => {
    sessionStorage.setItem("saved_action_resources_temp", JSON.stringify([]));
    setIsPatientRosterReminderAction(false);
    props.setModalOpen(false);
  };

  return (
    <GeneralModal
        open={props.modalOpen}
        title={"Create Quick Action"}
        successCallback={saveFollowUp}
        closeCallback={closeModal}
        containerClassName="manual-patient-modal-body"
        width="480px"
        confirmButtonText="Create Quick Action"
      >
        <Typography className="fum-text-content" variant="subtitle1">
          This action will appear in the Global Action Queue.
        </Typography>
        <Grid
          container
          className="fum-form-container"
          spacing={1}
          style={{ paddingTop: 20 }}
        >
          <Grid item xs={12} className="field-container">
            <InputLabel
              htmlFor="next-date"
              className="fum-field-label"
              sx={{ color: "#000", marginBottom: "10px" }}
            >
              Assign Due Date
            </InputLabel>
            <DatePickerWithLabel
              date={nextDate}
              setDate={setNextDate}
              initialDate={""}
              label="Assign Due Date"
            />
          </Grid>
          <Typography
            sx={{ marginLeft: "8px", marginTop: "10px" }}
            className="fum-text-content"
            variant="subtitle1"
          >
            Create Action
          </Typography>
          <Grid item xs={12} className="field-container">
            <TextField
              label="Title"
              placeholder="Enter Title"
              value={title}
              className="textInput"
              required
              maxRows={20}
              variant="filled"
              error={title.length > TITLE_MAX_LENGTH}
              helperText={
                title.length > TITLE_MAX_LENGTH
                  ? `Maximum length is ${TITLE_MAX_LENGTH} characters.`
                  : ""
              }
              onChange={(event) => {
                let input = event.target.value;
                if (input.length > TITLE_MAX_LENGTH) {
                  return;
                }

                setTitle(input);
              }}
              InputLabelProps={{
                required: true,
              }}
              InputProps={{
                disableUnderline: true,
              }}
              inputProps={{
                maxLength: TITLE_MAX_LENGTH,
              }}
            />
          </Grid>
          <Grid item xs={12} className="field-container">
           <TextField
              label="Description"
              placeholder="Enter Description"
              value={description}
              className="textInput"
              required
              multiline
              rows={4}
              maxRows={20}
              variant="filled"
              error={description.length > DESCRIPTION_MAX_LENGTH}
              helperText={
                description.length > DESCRIPTION_MAX_LENGTH
                  ? `Maximum length is ${DESCRIPTION_MAX_LENGTH} characters.`
                  : ""
              }
              onChange={(event) => {
                let input = event.target.value;
                if (input.length <= DESCRIPTION_MAX_LENGTH) {
                  setDescription(input);
                }
              }}
              inputProps={{
                maxLength: DESCRIPTION_MAX_LENGTH,
              }}
              InputLabelProps={{
                required: false,
              }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} className="field-container">
          <div style={{ textAlign: "center", marginTop: 10, marginBottom: 10 }}>
            {/* {checkPrivileges(userPrivileges, "Access Resource Catalog") && (
              <Link
                className="default-btn"
                onClick={addResource}
                style={{ paddingLeft: 90, paddingRight: 90 }}
              >
                Add Resource
              </Link>
            )} */}
            {actionResourceItems && actionResourceItems.length > 0 && (
              <>
                {actionResourceItems.map((actionResource, index) => (
                  <p
                    key={index}
                    style={{
                      textAlign: "left",
                      width: "75%",
                      marginLeft: "auto",
                      marginRight: "auto",
                      fontFamily: "QuicksandMedium",
                      fontSize: 14,
                    }}
                  >
                    <Link
                      href={`${actionResource.link}`}
                      target="_blank"
                      style={{ color: "#f8890b" }}
                    >
                      {actionResource.name}
                    </Link>
                    <Link
                      onClick={() => {
                        removeActionResourceItem(actionResource);
                      }}
                    >
                      <DeleteIcon
                        style={{
                          fontSize: 20,
                          color: "#c1b7b3",
                          display: "inline-block",
                          cursor: "pointer",
                          float: "right",
                        }}
                      />
                    </Link>
                  </p>
                ))}
              </>
            )}
          </div>
        </Grid>
        <Grid item xs={12} className="field-container" sx={{ paddingLeft: 0, paddingRight: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isPatientRosterReminderAction}
                onChange={(event) => setIsPatientRosterReminderAction(event.target.checked)}
                size="small"
              />
            }
            label="Patient Roster Reminder Action?"
          />
        </Grid>
      </GeneralModal>
  );
};

export default FollowUpForm;
